import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import Timer from './Timer';
import { playSound, stopSound } from './sound';

const REVEAL_HOLD_CORRECT = 1100;
const REVEAL_HOLD_WRONG = 850;
const RESULT_TOTAL_CORRECT = 3000;
const RESULT_TOTAL_WRONG = 2400;
const CORRECT_SOUND_FALLBACK_MS = 4300;

function ResultBurst({ result, teamColor, text, onDone }) {
  useEffect(() => {
    if (!result) return undefined;
    const hold = result
      ? Math.max(900, (result.isCorrect ? RESULT_TOTAL_CORRECT : RESULT_TOTAL_WRONG) - (result.revealHold || 0))
      : RESULT_TOTAL_WRONG;
    const timer = window.setTimeout(onDone, hold);
    return () => window.clearTimeout(timer);
  }, [onDone, result]);

  if (!result) return null;

  return (
    <motion.div
      className="result-burst-mobile"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="result-stage-mobile"
        initial={{ scale: 0.78, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', bounce: 0.32 }}
      >
        {result.isCorrect ? (
          <div className="result-duel-mobile">
            <motion.div
              className="result-coin-mobile"
              initial={{ scale: 0, rotate: -35 }}
              animate={{ scale: [0, 1.25, 1], rotate: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <span>★</span>
              <small>{text.point}</small>
            </motion.div>
            <motion.div
              className="result-hero-mobile"
              initial={{ x: 28, scale: 0.8 }}
              animate={{ x: [28, -10, 0], scale: 1 }}
              transition={{ duration: 0.58, ease: 'easeOut' }}
              style={{ borderColor: teamColor === 'blue' ? '#93c5fd' : '#fca5a5' }}
            >
              <img src="/saudi.png" alt="" />
            </motion.div>
          </div>
        ) : (
          <motion.div
            className="result-icon-mobile"
            animate={{ x: [0, -12, 12, -8, 8, 0] }}
            transition={{ duration: 0.38 }}
          >
            😅
          </motion.div>
        )}
        <div className="result-title-mobile">{result.isCorrect ? text.correctTitle : text.wrongTitle}</div>
        <div className="result-sub-mobile">
          {result.isCorrect ? text.correctSub : text.wrongSub}
        </div>
      </motion.div>
    </motion.div>
  );
}

function OptionAiHand() {
  return <span className="option-ai-hand-mobile">👆</span>;
}

export default function QuestionModal({
  question,
  team,
  isOpenPlay = false,
  openPlaySeconds = 10,
  isComputerTurn = false,
  computerAccuracy = 0.77,
  isMuted = false,
  text,
  onAnswer
}) {
  const totalSeconds = isOpenPlay ? openPlaySeconds : 40;
  const [timeLeft, setTimeLeft] = useState(totalSeconds);
  const [locked, setLocked] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [resultBurst, setResultBurst] = useState(null);
  const [computerChoice, setComputerChoice] = useState(null);
  const settledRef = useRef(false);
  const resultRef = useRef(null);
  const answerSoundRef = useRef(null);
  const soundDoneRef = useRef(true);
  const pendingDoneRef = useRef(false);
  const soundFallbackRef = useRef(null);
  const isRed = team.color === 'red';
  const accent = isRed ? '#dc2626' : '#2563eb';

  const finishResult = useCallback(() => {
    const result = resultRef.current;
    if (!result) return;

    if (result.isCorrect && !soundDoneRef.current) {
      pendingDoneRef.current = true;
      return;
    }

    onAnswer(result.isCorrect);
  }, [onAnswer]);

  const markSoundDone = useCallback(() => {
    window.clearTimeout(soundFallbackRef.current);
    soundDoneRef.current = true;
    if (pendingDoneRef.current) {
      pendingDoneRef.current = false;
      finishResult();
    }
  }, [finishResult]);

  const settle = useCallback((idx) => {
    if (settledRef.current) return;
    settledRef.current = true;
    setLocked(true);
    setSelected(idx);

    if (window.stopTimer) {
      window.stopTimer();
    }

    const isCorrect = idx === question.answer;
    resultRef.current = { isCorrect };
    soundDoneRef.current = true;
    pendingDoneRef.current = false;

    if (!isMuted) {
      const audio = playSound(isCorrect ? '/correct.wav' : '/wrong.wav');
      answerSoundRef.current = audio;

      if (isCorrect && audio) {
        soundDoneRef.current = false;
        audio.onended = markSoundDone;
        soundFallbackRef.current = window.setTimeout(markSoundDone, CORRECT_SOUND_FALLBACK_MS);
      }
    }

    setShowAnswer(true);

    window.setTimeout(() => {
      setResultBurst({ isCorrect, revealHold: isCorrect ? REVEAL_HOLD_CORRECT : REVEAL_HOLD_WRONG });
    }, isCorrect ? REVEAL_HOLD_CORRECT : REVEAL_HOLD_WRONG);
  }, [isMuted, markSoundDone, question.answer]);

  useEffect(() => {
    if (locked) return undefined;
    const timer = window.setInterval(() => {
      setTimeLeft(left => {
        const next = Math.max(0, left - 1);
        if (next === 0) {
          window.clearInterval(timer);
          settle(null);
        }
        return next;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [locked, settle]);

  useEffect(() => {
    if (!isComputerTurn || locked) return undefined;
    const thinkDelay = 1050 + Math.random() * 550;
    const answerDelay = thinkDelay + 2600 + Math.random() * 450;
    const shouldBeCorrect = Math.random() < computerAccuracy;
    const wrongOptions = question.options.map((_, i) => i).filter(i => i !== question.answer);
    const choice = shouldBeCorrect ? question.answer : wrongOptions[Math.floor(Math.random() * wrongOptions.length)];
    const handTimer = window.setTimeout(() => setComputerChoice(choice), thinkDelay);
    const answerTimer = window.setTimeout(() => settle(choice), answerDelay);
    return () => {
      window.clearTimeout(handTimer);
      window.clearTimeout(answerTimer);
    };
  }, [computerAccuracy, isComputerTurn, locked, question.answer, question.options, settle]);

  useEffect(() => {
    return () => {
      window.clearTimeout(soundFallbackRef.current);
      if (answerSoundRef.current) {
        answerSoundRef.current.onended = null;
      }
      stopSound(answerSoundRef.current);
    };
  }, []);

  const progress = Math.max(0, Math.min(100, (timeLeft / totalSeconds) * 100));
  const warning = timeLeft <= 5;

  return (
    <>
      <div className="question-backdrop-mobile" dir={text.dir}>
        {!resultBurst && (
          <motion.div
            className="question-card-mobile"
            initial={{ opacity: 0, scale: 0.78 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', bounce: 0.28 }}
          >
            <div className="question-header-mobile">
              <div className="question-team-pill" style={{ color: accent }}>
                <span className="question-team-dot" />
                <span>{text.questionFor} {team.name}</span>
              </div>
              <Timer seconds={totalSeconds} onTimeUp={() => settle(null)} isMuted={isMuted} />
            </div>

            <div className="question-progress-track">
              <div
                className="question-progress-fill"
                style={{ width: `${progress}%`, background: warning ? '#dc2626' : '#0f5f48' }}
              />
            </div>

            <div className="question-meta-mobile">
              {question.category && <span className="question-chip-mobile">{question.category}</span>}
              {question.difficulty && <span className="question-chip-mobile">{text.level} {question.difficulty}</span>}
              {isOpenPlay && <span className="question-chip-mobile">{text.openPlay}</span>}
            </div>

            <h2 className="question-text-mobile" dir="auto">{question.question}</h2>

            <div className="question-options-mobile">
              {question.options.map((option, idx) => {
                const isCorrect = showAnswer && idx === question.answer;
                const isWrong = showAnswer && selected === idx && selected !== question.answer;
                const isDimmed = showAnswer && !isCorrect && !isWrong;
                const isThinking = isComputerTurn && computerChoice === idx && !locked;
                return (
                  <motion.button
                    key={`${option}-${idx}`}
                    className={`question-option-mobile ${isCorrect ? 'correct' : ''} ${isWrong ? 'wrong' : ''} ${isDimmed ? 'dimmed' : ''}`}
                    initial={{ opacity: 0, y: 22, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: 0.1 + idx * 0.07, type: 'spring', bounce: 0.22 }}
                    disabled={locked || isComputerTurn}
                    onClick={() => settle(idx)}
                    style={isThinking ? { borderColor: '#d7ad4a', background: '#fff9e9' } : undefined}
                  >
                    <span className="question-option-label">{text.optionLabels[idx]}</span>
                    <span dir="auto">{option}</span>
                    {isThinking && <OptionAiHand />}
                    {isCorrect && <span className="question-option-result">✓</span>}
                    {isWrong && <span className="question-option-result">×</span>}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>

      <ResultBurst
        result={resultBurst}
        teamColor={team.color}
        text={text}
        onDone={finishResult}
      />
    </>
  );
}
